import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { num, dateShort } from "@/lib/format";
import { RunBadge, SkuTag } from "@/components/Badges";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Flame, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Production() {
  const [runs, setRuns] = useState([]);
  const [boms, setBoms] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ bom_code: "", facility: "", target_qty: "", scheduled_date: "" });
  const [completeRun, setCompleteRun] = useState(null);
  const [completeQty, setCompleteQty] = useState("");
  const [finishing, setFinishing] = useState(false);

  const load = () => api.get("/production-runs").then((r) => setRuns(r.data));
  const loadBoms = () => api.get("/boms").then((r) => setBoms(r.data));
  useEffect(() => {
    load(); loadBoms();
    api.get("/facilities").then((r) => setFacilities(r.data));
  }, []);

  const submit = async () => {
    if (!form.bom_code || !form.facility || !form.target_qty || !form.scheduled_date) {
      toast.error("Please fill in all fields"); return;
    }
    const bom = boms.find((b) => b.bom_code === form.bom_code);
    await api.post("/production-runs", { output_sku: bom.target_sku, bom_code: form.bom_code, facility: form.facility, target_qty: Number(form.target_qty), scheduled_date: form.scheduled_date });
    toast.success("Production run scheduled");
    setOpen(false);
    setForm({ bom_code: "", facility: "", target_qty: "", scheduled_date: "" });
    load();
  };

  const openComplete = (r) => { setCompleteRun(r); setCompleteQty(String(r.target_qty)); };

  const confirmComplete = async () => {
    const qty = Number(completeQty);
    if (!qty || qty <= 0) { toast.error("Enter a completed quantity"); return; }
    setFinishing(true);
    try {
      const { data } = await api.post(`/production-runs/${completeRun.batch_no}/complete`, { completed_qty: qty });
      toast.success(`Batch complete — produced ${num(data.produced)} units, consumed ${data.consumed.length} components`);
      setCompleteRun(null);
      await load(); await loadBoms();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not complete run");
    } finally {
      setFinishing(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
      <PageHeader title="Production Runs" subtitle="Kitchen batch scheduling — completing a run consumes ingredients and adds finished units"
        action={<Button data-testid="btn-create-production-run" onClick={() => setOpen(true)} className="bg-stone-900 hover:bg-stone-800 active:scale-[0.98]"><Plus className="h-4 w-4 mr-1.5" />New Run</Button>} />

      <div className="rounded-lg border border-stone-200 bg-white shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs font-semibold uppercase tracking-wider text-stone-500 bg-stone-50/80 border-b border-stone-200">
              <th className="px-4 py-3 text-left">Batch #</th>
              <th className="px-4 py-3 text-left">Output SKU</th>
              <th className="px-4 py-3 text-left">Facility</th>
              <th className="px-4 py-3 text-right">Target</th>
              <th className="px-4 py-3 text-right">Completed</th>
              <th className="px-4 py-3 text-left">Scheduled</th>
              <th className="px-4 py-3 text-left w-40">Yield</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.batch_no} data-testid={`run-row-${r.batch_no}`} className="border-b border-stone-100 last:border-0 hover:bg-stone-50/70 transition-colors">
                <td className="px-4 py-3"><SkuTag code={r.batch_no} /></td>
                <td className="px-4 py-3 font-medium text-stone-800">{r.output_name}</td>
                <td className="px-4 py-3 text-stone-600">{r.facility_name}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums">{num(r.target_qty)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums">{num(r.completed_qty)}</td>
                <td className="px-4 py-3 text-stone-600">{dateShort(r.scheduled_date)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-stone-200 overflow-hidden">
                      <div className="h-full bg-violet-500 rounded-full" style={{ width: `${r.yield_pct}%` }} />
                    </div>
                    <span className="text-xs font-mono text-stone-500">{r.yield_pct}%</span>
                  </div>
                </td>
                <td className="px-4 py-3"><RunBadge status={r.status} /></td>
                <td className="px-4 py-3 text-right">
                  {r.status !== "complete" ? (
                    <Button size="sm" variant="outline" data-testid={`btn-complete-${r.batch_no}`} onClick={() => openComplete(r)} className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Complete
                    </Button>
                  ) : <span className="text-xs text-stone-400">Done</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* schedule dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Flame className="h-5 w-5 text-violet-600" />Schedule Production Run</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-stone-500">Bill of Materials</Label>
              <Select value={form.bom_code} onValueChange={(v) => setForm({ ...form, bom_code: v })}>
                <SelectTrigger data-testid="run-bom-select" className="mt-1"><SelectValue placeholder="Select recipe" /></SelectTrigger>
                <SelectContent>{boms.map((b) => <SelectItem key={b.bom_code} value={b.bom_code}>{b.target_name} ({b.bom_code})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-stone-500">Facility</Label>
              <Select value={form.facility} onValueChange={(v) => setForm({ ...form, facility: v })}>
                <SelectTrigger data-testid="run-facility-select" className="mt-1"><SelectValue placeholder="Select facility" /></SelectTrigger>
                <SelectContent>{facilities.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-stone-500">Target Qty</Label><Input data-testid="run-target-input" type="number" value={form.target_qty} onChange={(e) => setForm({ ...form, target_qty: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs text-stone-500">Scheduled Date</Label><Input data-testid="run-date-input" type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="mt-1" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="run-submit-btn" onClick={submit} className="bg-stone-900 hover:bg-stone-800">Schedule Run</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* complete dialog */}
      <Dialog open={!!completeRun} onOpenChange={(o) => !o && !finishing && setCompleteRun(null)}>
        <DialogContent className="bg-white">
          {completeRun && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600" />Complete {completeRun.batch_no}</DialogTitle></DialogHeader>
              <div className="space-y-3 py-1 text-sm">
                <p className="text-stone-500">Completing <span className="font-medium text-stone-700">{completeRun.output_name}</span> at {completeRun.facility_name} will <span className="font-semibold">consume the BOM ingredients & packaging</span> from that facility and <span className="font-semibold">add the finished units</span> to stock.</p>
                <div>
                  <Label className="text-xs text-stone-500">Completed quantity (actual yield)</Label>
                  <Input data-testid="complete-qty-input" type="number" value={completeQty} onChange={(e) => setCompleteQty(e.target.value)} className="mt-1" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCompleteRun(null)} disabled={finishing}>Cancel</Button>
                <Button data-testid="complete-submit-btn" onClick={confirmComplete} disabled={finishing} className="bg-emerald-700 hover:bg-emerald-800">{finishing ? "Processing…" : "Complete & Post"}</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
